/**
 * Loads Trustpilot TrustBox bootstrap once (singleton).
 * @see https://help.trustpilot.com/s/article/Add-a-TrustBox-widget-to-a-single-page-application
 */
import {useEffect, useState} from 'react';

const TRUSTPILOT_BOOTSTRAP_URL =
  'https://widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js';

const trustpilotScriptManager = {
  ready: false,
  loadPromise: null,

  /**
   * @returns {Promise<void>}
   */
  load() {
    if (typeof window === 'undefined') {
      return Promise.reject(new Error('Trustpilot bootstrap requires a browser'));
    }

    if (this.ready && window.Trustpilot) {
      return Promise.resolve();
    }

    if (this.loadPromise) {
      return this.loadPromise;
    }

    this.loadPromise = new Promise((resolve, reject) => {
      const existing = document.querySelector('script[data-trustpilot-bootstrap]');

      if (existing) {
        if (window.Trustpilot) {
          this.ready = true;
          resolve();

          return;
        }

        existing.addEventListener('load', () => {
          this.ready = true;
          resolve();
        });
        existing.addEventListener('error', () => {
          this.loadPromise = null;
          reject(new Error('Failed to load Trustpilot bootstrap'));
        });

        return;
      }

      const script = document.createElement('script');

      script.async = true;
      script.src = TRUSTPILOT_BOOTSTRAP_URL;
      script.setAttribute('data-trustpilot-bootstrap', 'true');
      script.onload = () => {
        this.ready = true;
        resolve();
      };
      script.onerror = () => {
        this.loadPromise = null;
        reject(new Error('Failed to load Trustpilot bootstrap'));
      };

      document.head.appendChild(script);
    });

    return this.loadPromise;
  },
};

/**
 * @param {boolean} [enabled=true]
 * @returns {boolean}
 */
export function useTrustpilotScript(enabled = true) {
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    if (!enabled) {
      setIsReady(false);

      return undefined;
    }

    let cancelled = false;

    trustpilotScriptManager
      .load()
      .then(() => {
        if (!cancelled) {
          setIsReady(true);
        }
      })
      .catch((error) => {
        if (!cancelled) {
          console.error('[Trustpilot] bootstrap load failed', error);
          setIsReady(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [enabled]);

  return isReady;
}
